import { ForwardedRef, forwardRef, useEffect } from 'react'

import Checkbox, { CheckboxInputProps } from '../inputs/checkbox.input'
import FieldSet from './field-set'
import { FormFieldProps } from './types'
import { onBlurHandler, useRemoveFieldOnUnmount } from './utils'

export type CheckboxFieldProps = Omit<CheckboxInputProps, 'onChange'> &
  FormFieldProps<boolean> & {
    onChange?: (checked: boolean, value?: string) => void
    link?: string | null
  }

const CheckboxField = (
  {
    form,
    name,
    wrapperClassName,
    isArrayField,
    arrayFieldName,
    arrayFieldIndex,
    label,
    labelExtra,
    description,
    validators,
    link = null,
    onChange,
    onBlur,
    removeFieldOnUnmount,
    checked,
    ...rest
  }: CheckboxFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  useRemoveFieldOnUnmount(form, name, removeFieldOnUnmount)

  // Sync checked prop with form field value for prop-controlled usage
  useEffect(() => {
    if (form && checked !== undefined) {
      form.setFieldValue(name, checked)
    }
  }, [checked, form, name])

  return (
    <FieldSet
      name={name}
      form={form}
      wrapperClassName={wrapperClassName}
      arrayFieldIndex={arrayFieldIndex}
      arrayFieldName={arrayFieldName}
      isArrayField={isArrayField}
      {...rest}
    >
      {(hasErrors) => (
        <form.Field name={name} validators={validators}>
          {(field: any) => {
            return (
              <Checkbox
                {...rest}
                ref={ref}
                name={name}
                checked={field.state.value ?? false}
                descriptionLink={link}
                onBlur={onBlurHandler(field, onBlur)}
                onChange={(newChecked: boolean, value?: string) => {
                  onChange?.(newChecked, value)
                  field.handleChange(newChecked)
                }}
                label={label}
                labelExtra={labelExtra}
                description={description}
                hasErrors={hasErrors}
              />
            )
          }}
        </form.Field>
      )}
    </FieldSet>
  )
}

export default forwardRef<HTMLInputElement, CheckboxFieldProps>(CheckboxField)
